import React from "react";

/**
 * Formats a suffix with conditional period (only for Sr or Jr)
 * @param {string} suffix - Suffix value (e.g., "Sr", "Jr", "III", etc.)
 * @returns {string} - Formatted suffix with period only for Sr or Jr (e.g., "Sr." or "III")
 */
export const formatSuffix = (suffix) => {
  if (!suffix) return "";
  const suffixUpper = suffix.trim().toUpperCase();
  // Only add period for Sr or Jr
  if (suffixUpper === "SR" || suffixUpper === "JR") {
    return `${suffix.trim()}.`;
  }
  return suffix.trim();
};

/**
 * Short display name for family tree nodes / Heads of Line style: first name, then suffix if present, else special name if present, else just first name.
 * @param {string} firstName - First name
 * @param {string|object} suffix - Suffix (string or { name: string })
 * @param {string} specialName - Special name or initial (e.g. "M", "UnNamed Twin")
 * @returns {string} - e.g. "Roger, Sr." or "Mary M" or "Ida"
 */
export const getShortDisplayName = (firstName, suffix, specialName) => {
  const first = (firstName || "").trim();
  const suffixVal = (suffix && (typeof suffix === "object" && suffix !== null ? suffix.name : suffix)) || "";
  const special = (specialName || "").trim();
  if (!first) return "";
  if (suffixVal) return `${first}, ${formatSuffix(suffixVal)}`;
  if (special) return `${first} ${special}`;
  return first;
};

/**
 * Placeholder first names given to members not named yet — "UnNamed Boy",
 * "UnNamed Girl 2". Their first name says nothing about whose they are, so
 * rows that otherwise print a first name alone add the last name for them.
 * @param {string} firstName
 * @returns {boolean}
 */
export const isUnnamedFirstName = (firstName) =>
  /^un-?named\b/i.test(String(firstName ?? "").trim());

const NAME_SUFFIX_PATTERN =
  /(?:,\s*|\s+)(jr|sr|ii|iii|iv|v|vi|vii|viii|ix|x|esq|phd|md|cpa|dds|dvm)\.?\s*$/i;

/**
 * A member's suffix: the suffix field when the API sends one, otherwise read off
 * the end of the full name (some endpoints only fold it into `name`).
 * @param {object} member - Member / invitee record
 * @returns {string} - Raw suffix (e.g. "Jr", "III") or ""
 */
export const getMemberSuffix = (member) => {
  if (!member) return "";
  const raw = member.suffix ?? member.member_suffix;
  const value = raw && typeof raw === "object" ? raw.name ?? raw.value : raw;
  if (value && String(value).trim()) return String(value).trim();

  for (const full of [member.name, member.full_name, member.member_name]) {
    if (typeof full !== "string") continue;
    const match = full.trim().match(NAME_SUFFIX_PATTERN);
    // Needs a name in front of it, so a lone "V" isn't read as a suffix
    if (match && full.trim().length > match[0].length) return match[1];
  }
  return "";
};

/**
 * Formats a full name from name parts with a comma before the suffix
 * @param {string} firstName - First name
 * @param {string} middleName - Middle name (optional)
 * @param {string} lastName - Last name (optional)
 * @param {string} suffix - Suffix like Sr., Jr., III, etc. (optional)
 * @returns {string} - Formatted name with comma before suffix (e.g., "Frank Witherspoon, Sr." or "Frank Witherspoon, III")
 */
export const formatNameWithSuffix = (firstName, middleName, lastName, suffix) => {
  const nameParts = [firstName, middleName, lastName].filter(Boolean);
  const baseName = nameParts.join(" ");
  
  if (suffix) {
    // Use formatSuffix to only add period for Sr or Jr
    const formattedSuffix = formatSuffix(suffix);
    return baseName ? `${baseName}, ${formattedSuffix}` : formattedSuffix;
  }
  
  return baseName || "";
};

/**
 * Renders a name with the first word bold and gender-based color
 * @param {string} name - The full name to render
 * @param {number|string} genderValue - Gender value (1 for male, 2 for female, etc.)
 * @param {string} maleColor - Color for male (default: "#0000FF" - blue)
 * @param {string} femaleColor - Color for female (default: "#FF0000" - red)
 * @returns {JSX.Element|null} - React elements or null if name is empty
 */
export const renderNameWithBoldFirst = (name, genderValue, maleColor = "#0000FF", femaleColor = "#FF0000") => {
  if (!name) return null;
  
//   const nameParts = name.split(" ");
//   const firstName = nameParts[0];
//   const restOfName = nameParts.slice(1).join(" ");
//   const nameColor = genderValue == 1 ? maleColor : femaleColor;
  // Trim the name and split, filtering out empty strings to handle multiple spaces
  const trimmedName = name.trim();
  const nameParts = trimmedName.split(/\s+/).filter(part => part.length > 0);
  const firstName = nameParts[0];
  const restOfName = nameParts.slice(1).join(" ");
  const nameColor = genderValue == 1 ? maleColor : femaleColor;
  
  return (
    <>
      <span className="name first-name" style={{ color: nameColor, fontWeight: 900 }}>
        {firstName}&nbsp;{restOfName ? " " : ""}
      </span>
      {restOfName && (
        <span className="name rest-names" style={{ color: nameColor }}>
          {restOfName}
        </span>
      )}
    </>
  );
};